import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as bookingApi from '@/services/bookingApi';

interface PhoneVerificationProps {
  bookingId: string;
  phone?: string;
  onVerified: (status: string) => void;
  onBackToProfile: () => void;
}

export const PhoneVerification: React.FC<PhoneVerificationProps> = ({
  bookingId,
  phone,
  onVerified,
  onBackToProfile,
}) => {
  const { t } = useTranslation('booking');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;

    setStatus('submitting');
    setErrorMessage(null);

    try {
      const result = await bookingApi.verifyPhone({ booking_id: bookingId, code });
      onVerified(result.status);
    } catch (err: unknown) {
      setStatus('error');
      setErrorMessage(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          t('phoneVerification.invalidCode')
      );
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="text-6xl mb-4">📱</div>
        <h2 className="text-2xl font-bold text-gray-900">{t('phoneVerification.title')}</h2>
        <p className="text-gray-600 mt-2">
          {t('phoneVerification.subtitle', { phone: phone || t('customer.form.phone.label') })}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('phoneVerification.codeLabel')}
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full px-4 py-3 text-center text-2xl tracking-widest rounded-lg border"
            placeholder={t('phoneVerification.codePlaceholder')}
            autoComplete="one-time-code"
          />
        </div>

        {errorMessage && (
          <p className="text-sm text-red-600">{errorMessage}</p>
        )}

        <button
          type="submit"
          disabled={code.length !== 6 || status === 'submitting'}
          className="w-full px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:bg-gray-300"
        >
          {status === 'submitting' ? t('buttons.loading') : t('phoneVerification.verifyButton')}
        </button>
      </form>

      <div className="text-center">
        <button
          type="button"
          onClick={onBackToProfile}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          {t('emailVerification.backToProfile')}
        </button>
      </div>
    </div>
  );
};
